/**
 * IBAN of any country, checked by ibankit.
 * @module banking/schemes/iban
 */

import { validateIBAN } from '../validation';
import { BankAccountScheme, type BankAccountSchemeDefinition } from './bank-account-scheme';
import { bicErrors, compactIban, countryMismatchErrors, normalizeBic } from './scheme-helpers';

function cleanIban(iban?: string | null): string | null {
  const clean = compactIban(iban);
  return clean && validateIBAN(clean).valid ? clean : null;
}

export const ibanScheme: BankAccountSchemeDefinition = {
  scheme: BankAccountScheme.IBAN,

  validate(account) {
    const { errors } = validateIBAN(compactIban(account.iban) ?? '');
    const iban = cleanIban(account.iban);
    if (iban) errors.push(...countryMismatchErrors(account.countryCode, iban.slice(0, 2)));
    errors.push(...bicErrors(account.bic));

    return { valid: errors.length === 0, errors };
  },

  normalize(account) {
    const iban = cleanIban(account.iban);
    if (!iban) return null;

    return {
      scheme: BankAccountScheme.IBAN,
      countryCode: iban.slice(0, 2),
      iban,
      bankIdentifier: null,
      accountNumber: null,
      bic: normalizeBic(account.bic)
    };
  },

  toIban(account) {
    return cleanIban(account.iban);
  }
};
